/**
 * Shared presentational primitives for admin pages. Built on the vendored Nomad
 * design tokens (border-subtle / surface-foreground / content-* etc.) so every
 * admin surface looks consistent and the merge into andavo-dev is a token
 * re-point, not a restyle. NO inline scripts, NO on*= attributes — React event
 * props only (rebuild-lessons.md #1, inherent to React).
 */
import type { ReactNode } from 'react';
import { cn } from '@andavo/web-ui';

/** Page header: title + optional subtitle + right-aligned actions slot. */
export function AdminPageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-4 flex items-start justify-between gap-4">
      <div>
        <h1 className="font-sans font-semibold text-content-primary text-lg">
          {title}
        </h1>
        {subtitle ? (
          <p className="mt-0.5 font-inter text-content-secondary text-sm">
            {subtitle}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      ) : null}
    </div>
  );
}

/** A bordered card with an optional heading + actions row. */
export function Panel({
  title,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        'rounded-md border border-border-subtle bg-surface-foreground',
        className,
      )}
    >
      {title || actions ? (
        // `min-w-0 + truncate` on the title so a long heading (e.g. the
        // RoleSetEditor's `Roles — long.email@andavolabs.com`) can't push the
        // action buttons off the edge of a 390px phone — it truncates instead.
        // `shrink-0` keeps the actions (Reset / Save) at full size and reachable.
        <header className="flex items-center justify-between gap-2 border-border-subtle border-b px-4 py-2.5">
          <h2 className="min-w-0 truncate font-sans font-medium text-content-primary text-sm">
            {title}
          </h2>
          {actions ? (
            <div className="flex shrink-0 items-center gap-2">{actions}</div>
          ) : null}
        </header>
      ) : null}
      <div className="p-4">{children}</div>
    </section>
  );
}

type Tone = 'ok' | 'warn' | 'error' | 'muted';

const DOT_TONE: Record<Tone, string> = {
  ok: 'bg-green-500',
  warn: 'bg-amber-500',
  error: 'bg-red-500',
  muted: 'bg-content-tertiary',
};

/** A small colored status dot with an accessible label. */
export function StatusDot({ tone, label }: { tone: Tone; label?: string }) {
  return (
    <span
      aria-label={label}
      className={cn('inline-block size-2 rounded-full', DOT_TONE[tone])}
      role={label ? 'img' : undefined}
    />
  );
}

/** Map a free-form status/conclusion string onto a dot tone. */
export function toneForStatus(status: string | null | undefined): Tone {
  const s = (status ?? '').toLowerCase();
  if (['success', 'ok', 'healthy', 'closed', 'pass', 'completed'].includes(s)) {
    return 'ok';
  }
  if (
    [
      'running',
      'degraded',
      'half-open',
      'pending',
      'in_progress',
      'warning',
    ].includes(s)
  ) {
    return 'warn';
  }
  if (
    ['failure', 'failed', 'down', 'outage', 'open', 'error', 'fail'].includes(s)
  ) {
    return 'error';
  }
  return 'muted';
}

/** Centered loading state — shared by every page's pending branch. */
export function AdminLoading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div
      aria-busy="true"
      className="flex items-center gap-2 py-8 font-inter text-content-secondary text-sm"
    >
      {label}
    </div>
  );
}

/** Inline error state — shared by every page's error branch. */
export function AdminError({ message }: { message: string }) {
  return (
    <div
      className="rounded-md border border-red-500/40 bg-red-500/5 px-3 py-2 font-inter text-red-600 text-sm"
      role="alert"
    >
      {message}
    </div>
  );
}

/** Empty state. */
export function AdminEmpty({ children }: { children: ReactNode }) {
  return (
    <div className="py-8 text-center font-inter text-content-tertiary text-sm">
      {children}
    </div>
  );
}

/** A degraded-section banner: the backend returned PENDING / { error } for a tile. */
export function PendingBanner({ note }: { note?: string }) {
  return (
    <div className="rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 font-inter text-amber-700 text-sm">
      Not available yet{note ? ` — ${note}` : ''}.
    </div>
  );
}

/** Minimal, accessible table wrapper. Columns are caller-defined cells. */
export function Table({
  head,
  children,
}: {
  head: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse font-inter text-sm">
        <thead>
          <tr className="border-border-subtle border-b text-left text-content-secondary text-xs">
            {head}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Th({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <th className={cn('px-2 py-1.5 font-medium', className)}>{children}</th>
  );
}

export function Td({
  children,
  className,
  title,
  colSpan,
}: {
  children: ReactNode;
  className?: string;
  title?: string;
  /** For detail rows that span the remaining columns (e.g. an expanded row). */
  colSpan?: number;
}) {
  return (
    <td
      className={cn('px-2 py-1.5 align-top text-content-primary', className)}
      colSpan={colSpan}
      title={title}
    >
      {children}
    </td>
  );
}

export function Tr({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <tr
      className={cn(
        'border-border-subtle/60 border-b last:border-0',
        className,
      )}
    >
      {children}
    </tr>
  );
}

/** Monospaced inline code. */
export function Mono({
  children,
  title,
}: {
  children: ReactNode;
  title?: string;
}) {
  return (
    <span className="font-mono text-content-primary text-xs" title={title}>
      {children}
    </span>
  );
}
